import request from 'api/requestUtils'
import type { paths } from '@luxuryescapes/contract-cart'
import config from 'constants/config'

type CreateRequest = paths['/api/carts/cart-offer-comparison']['post']['requestBody']['content']['application/json']
type CreateResponse = paths['/api/carts/cart-offer-comparison']['post']['responses']['200']['content']['application/json']
type GetResponse = paths['/api/carts/cart-offer-comparison']['get']['responses']['200']['content']['application/json']

export type CompareCartItem = {
  userId: string
  items: Array<App.OfferComparisonSelection>
}

export type CompareCartItemsResponse = {
  status: number
  result: CompareCartItem
}

export type CreateCompareCartItemResponse = {
  status: number
  result: CompareCartItem
}

export function mapToOfferType(offerType: string): App.OfferType {
  switch (offerType) {
    case 'bedbank_hotel':
      return 'bedbank_hotel'
    case 'hotel':
      return 'hotel'
    case 'tactical_ao_hotel':
      return 'tactical_ao_hotel'
    default:
      return 'hotel'
  }
}

function mapToOfferComparisonSelection(items?: Array<CreateRequest['items'][number]>): Array<App.OfferComparisonSelection> {
  if (!items) return []
  return items.map(item => ({
    selectionId: item.selectionId,
    offerType: mapToOfferType(item.offerType),
    offerId: item.offerId,
    name: item.name,
    label: item.label ?? undefined,
    leadOfferImageId: item.leadOfferImageId ?? undefined,
    priceTotals: {
      price: item.priceTotals?.price ?? 0,
      memberPrice: item.priceTotals?.memberPrice ?? 0,
      value: item.priceTotals?.value ?? 0,
    },
    params: {
      packageUniqueKey: item.params?.packageUniqueKey ?? undefined,
      roomId: item.params?.roomId ?? undefined,
      packageId: item.params?.packageId ?? undefined,
      filters: item.params?.filters as { [key: string]: unknown },
    },
  }))
}

function mapResponse(response: GetResponse | CreateResponse): CompareCartItemsResponse | CreateCompareCartItemResponse {
  const { result, status } = response
  return {
    result: {
      userId: result?.userId ?? '',
      items: mapToOfferComparisonSelection(result?.items ?? undefined),
    },
    status,
  }
}

export async function getActiveComparisonItems(): Promise<CompareCartItemsResponse> {
  const result = await request.get<GetResponse>('/api/carts/cart-offer-comparison', { credentials: 'include' })
  return mapResponse(result)
}

export async function createCartOfferComparison(items: Array<App.OfferComparisonSelection>, cartId?: string): Promise<CreateCompareCartItemResponse> {
  const basePayload: CreateRequest = {
    brand: config.BRAND,
    items: items.map(item => ({
      selectionId: item.selectionId,
      offerType: item.offerType,
      offerId: item.offerId,
      name: item.name,
      label: item.label,
      leadOfferImageId: item.leadOfferImageId,
      priceTotals: {
        price: item.priceTotals?.price ?? 0,
        memberPrice: item.priceTotals?.memberPrice ?? 0,
        value: item.priceTotals?.value ?? 0,
      },
      params: {
        packageUniqueKey: item.params?.packageUniqueKey,
        roomId: item.params?.roomId,
        packageId: item.params?.packageId,
        filters: item.params?.filters as { [key: string]: unknown },
      },
    })),
  }

  const payload = { ...basePayload, ...(cartId ? { cartId } : {}) }
  const result = await request.post<CreateResponse, CreateRequest>('/api/carts/cart-offer-comparison', payload, { credentials: 'include' })
  return mapResponse(result)
}
